import { useMemo } from 'react';

import { Chip, Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';

import { routes } from '@/constants/routes';
import type { ColumnWidths } from '@/hooks/grid';
import { formatEnumLabel } from '@/lib/resource-config';
import { useLocaleStore } from '@/stores/locale';
import { createDateColumn } from '@/utils/dataGrid';
import { formatDate } from '@/utils/date';

export const useColumns = (columnWidths?: ColumnWidths): GridColDef[] => {
  const { t } = useTranslation('dashboardsOverview');
  const { lang } = useLocaleStore();
  const { pathname, search } = useLocation();

  return useMemo(() => {
    const baseColumns: GridColDef[] = [
      {
        field: 'DashboardType',
        headerName: t('columns.Type'),
        display: 'flex',
        minWidth: 80,
        width: 140,
        renderCell: (params) => {
          // dashboard type is an enum, so show translated label instead of raw value
          const typeLabel = params.value
            ? formatEnumLabel(params.value.toString(), 'dashboardsOverview')
            : '';
          return params.value === 'System' ? (
            <Stack direction="row" alignItems="center">
              <Typography variant="labelMono" color="secondary">
                {typeLabel}
              </Typography>
              <Chip
                label={t('ViewOnlyChip')}
                color="purple"
                size="small"
                monoText
              />
            </Stack>
          ) : (
            <Typography variant="labelMono" color="secondary">
              {typeLabel}
            </Typography>
          );
        },
      },
      {
        field: 'Name',
        headerName: t('columns.DashboardName'),
        flex: 1,
        minWidth: 300,
        hideable: false,
        renderCell: (params) => (
          <Link
            to={routes.reporting.dashboard(params.row.SourceId)}
            state={{ backTo: pathname + search }} // pass current location as state for back navigation in DashboardPageHeader
          >
            {params.value}
          </Link>
        ),
      },
      {
        field: 'OwnerId',
        headerName: t('columns.Author'),
        width: 200,
        renderCell: (params) => {
          return <span>{params.row.OwnerName ?? '-'}</span>;
        },
      },
      {
        field: 'CreatedAt',
        headerName: t('columns.CreatedAt'),
        ...createDateColumn(lang),
        // use defaults from createDateColumn but override renderCell to show '-' if owner is TimeLog (template)
        renderCell: (params) => {
          return (
            <span>
              {params.row.OwnerId === 0
                ? '-'
                : formatDate(params.row.CreatedAt, lang)}
            </span>
          );
        },
      },
      {
        field: 'SharingScope',
        headerName: t('columns.SharedWith'),
        display: 'flex',
        minWidth: 150,
        renderCell: (params) => {
          // sharing scope is an enum, so show translated label instead of raw value
          const typeLabel = params.value
            ? formatEnumLabel(params.value.toString(), 'dashboardsOverview')
            : '';
          return (
            <Typography variant="labelMono" color="secondary">
              {typeLabel}
            </Typography>
          );
        },
      },
    ];

    // Apply stored widths to columns if available
    if (columnWidths) {
      return baseColumns.map((col) => {
        const storedWidth = columnWidths[col.field];
        if (
          storedWidth !== undefined &&
          Number.isFinite(storedWidth) &&
          storedWidth >= 0
        ) {
          // Remove flex and apply stored width
          const { flex: _flex, ...colWithoutFlex } = col;
          return { ...colWithoutFlex, width: storedWidth };
        }
        return col;
      });
    }

    return baseColumns;
  }, [t, columnWidths, lang, pathname, search]);
};
